import moment from 'moment';
import { Translate } from 'react-admin';
import { ExpiryTimeGranularityType, ExpiryTimeGranularity } from '@x-guard/xgac-types/xgac';

// Month starts from 28 days when rounded to the nearest time granularity as the number of days in a month can vary
// The exact values are used for calculating the milliseconds to the nearest time granularity, so the months are always 30 days
const expiryTimeGranularityMap = (rounded = true) => {

  return {
    day: 1000 * 60 * 60 * 24,
    week: 1000 * 60 * 60 * 24 * 7,
    month: rounded ? 1000 * 60 * 60 * 24 * 28 : 1000 * 60 * 60 * 24 * 30,
    year: 1000 * 60 * 60 * 24 * 365,
  };

};

/**
 * The general pattern for 'rounding' milliseconds to the nearest time granularity is either displaying the largest period fitting in the milliseconds,
 * or the next one after the largest fitting one.
 * For example, if a month(s) fit in the milliseconds, and the remainder is less than a week, then the month(s) is(are) displayed.
 * If the remainder is more than a week, weeks are displayed.
 * The only exception to this pattern is year, because it is easier to comprehend years in terms of months, not quarters.
 */
export const getExpiryTimeGranularity = (startDate: string, expiryDate: string): ExpiryTimeGranularity => {

  let displayPeriod: ExpiryTimeGranularityType = ExpiryTimeGranularityType.Month;
  const startDateMoment = moment(startDate).startOf('day');
  const expiryDateMoment = moment(expiryDate).startOf('day');
  const milliseconds = expiryDateMoment.diff(startDateMoment);

  if (milliseconds >= expiryTimeGranularityMap().year) {

    const remainingMilliseconds = milliseconds % expiryTimeGranularityMap().year;
    if (remainingMilliseconds >= expiryTimeGranularityMap().month) {

      displayPeriod = ExpiryTimeGranularityType.Month;

    } else {

      displayPeriod = ExpiryTimeGranularityType.Year;

    }

  } else if (milliseconds >= expiryTimeGranularityMap().month) {

    const remainingMilliseconds = milliseconds % expiryTimeGranularityMap().month;
    if (remainingMilliseconds >= expiryTimeGranularityMap().week) {

      displayPeriod = ExpiryTimeGranularityType.Week;

    } else {

      displayPeriod = ExpiryTimeGranularityType.Month;

    }

  } else if (milliseconds >= expiryTimeGranularityMap().week) {

    const remainingMilliseconds = milliseconds % expiryTimeGranularityMap().week;
    if (remainingMilliseconds >= expiryTimeGranularityMap().day) {

      displayPeriod = ExpiryTimeGranularityType.Day;

    } else {

      displayPeriod = ExpiryTimeGranularityType.Week;

    }

  } else {

    displayPeriod = ExpiryTimeGranularityType.Day;

  }

  const value = Math.floor(milliseconds / expiryTimeGranularityMap()[displayPeriod]);
  return { type: displayPeriod, value };

};

export const TimeGranularityLabel = (timeGranularity: ExpiryTimeGranularity | undefined, translate: Translate) => {

  if (!timeGranularity) {

    return translate('general.text.none');

  }

  return translate(`general.text.time_granularity_types.${timeGranularity.type}`, { smart_count: timeGranularity.value });

};

export const convertTimeGranularityToDays = (timeGranularity: ExpiryTimeGranularity): number => {

  return (timeGranularity.value * expiryTimeGranularityMap(false)[timeGranularity.type]) / expiryTimeGranularityMap(false)[ExpiryTimeGranularityType.Day];

};

export const calculateLargestReminderPeriodInDays = (
  reminderPeriods: ExpiryTimeGranularity[],
): number => Math.max(0, ...(reminderPeriods ?? []).map(convertTimeGranularityToDays));
